import type { OrderRow } from '../../api/amazonconnect';
import type { OrdersMetricsBucket } from '../../api/amazonconnect';
import { parseOrderTotal } from '../daily-brief/format';
import { calendarDay, hourInTz } from '../ordersTruth';
import type { HourBucket, HourPoint, PulseMetricId, SalesPulseModel } from './types';
import { PULSE_SERIES } from './types';

function startOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function hourLabel(h: number): string {
  return `${String(h).padStart(2, '0')}:00`;
}

function emptyBucket(hour: number): HourBucket {
  return {
    hour,
    label: hourLabel(hour),
    gmv: 0,
    orders: 0,
    units: 0,
    multiCurrency: false,
  };
}

/** Bucket one local calendar day into 24 hour slots from LIVE order headers. */
export function bucketDay(orders: OrderRow[], dayStart: Date): HourBucket[] {
  const start = startOfLocalDay(dayStart).getTime();
  const end = start + 24 * 3_600_000 - 1;
  const buckets = Array.from({ length: 24 }, (_, h) => emptyBucket(h));
  const sales = Array.from({ length: 24 }, () => 0);
  const salesCount = Array.from({ length: 24 }, () => 0);
  const unitsSum = Array.from({ length: 24 }, () => 0);
  const unitsCount = Array.from({ length: 24 }, () => 0);
  const ccy = Array.from({ length: 24 }, () => new Set<string>());

  for (const o of orders) {
    if (!o.purchase_date) continue;
    const t = Date.parse(o.purchase_date);
    if (!Number.isFinite(t) || t < start || t > end) continue;
    const h = new Date(t).getHours();
    buckets[h].orders += 1;
    const total = parseOrderTotal(o.order_total);
    if (total != null) {
      sales[h] += total;
      salesCount[h] += 1;
      if (o.currency) ccy[h].add(o.currency.toUpperCase());
    }
    if (typeof o.quantity === 'number' && o.quantity > 0) {
      unitsSum[h] += o.quantity;
      unitsCount[h] += 1;
    }
  }

  for (let h = 0; h < 24; h++) {
    const multi = ccy[h].size > 1;
    buckets[h].multiCurrency = multi;
    if (multi) {
      buckets[h].gmv = null;
    } else if (salesCount[h] === 0) {
      buckets[h].gmv = buckets[h].orders === 0 ? 0 : null;
    } else {
      buckets[h].gmv = sales[h];
    }
    if (unitsCount[h] === 0) {
      buckets[h].units = buckets[h].orders === 0 ? 0 : null;
    } else {
      buckets[h].units = unitsSum[h];
    }
  }
  return buckets;
}

export function bucketDayFromSeries(
  buckets: OrdersMetricsBucket[],
  dayStart: Date,
  tz = 'Asia/Kolkata',
): HourBucket[] {
  const dayIso = isoDate(startOfLocalDay(dayStart));
  const out = Array.from({ length: 24 }, (_, h) => emptyBucket(h));
  for (const b of buckets) {
    if (!b.start) continue;
    if (calendarDay(b.start, tz) !== dayIso) continue;
    const h = hourInTz(b.start, tz);
    if (h < 0 || h > 23) continue;
    out[h].gmv = (out[h].gmv ?? 0) + (Number(b.sales) || 0);
    out[h].orders += Number(b.order_items) || 0;
    out[h].units = (out[h].units ?? 0) + (Number(b.units) || 0);
  }
  return out;
}

function avgAcross(
  days: HourBucket[][],
  hour: number,
  metric: 'gmv' | 'orders' | 'units',
): number | null {
  if (days.length === 0) return null;
  let sum = 0;
  let n = 0;
  for (const day of days) {
    const b = day[hour];
    if (!b) continue;
    const v = metric === 'gmv' ? b.gmv : metric === 'units' ? b.units : b.orders;
    if (v == null) continue;
    sum += v;
    n += 1;
  }
  if (n === 0) return null;
  return sum / n;
}

export function buildSalesPulseModel(input: {
  connectionId: string | null;
  marketplaceId?: string;
  sellerRef?: string;
  orders: OrderRow[];
  series?: OrdersMetricsBucket[];
  truncated: boolean;
  error: string | null;
  source?: string;
  now?: Date;
}): SalesPulseModel {
  const now = input.now ?? new Date();
  const todayStart = startOfLocalDay(now);
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);
  const series = input.series;
  const dayBuckets = (start: Date) =>
    series ? bucketDayFromSeries(series, start) : bucketDay(input.orders, start);

  const today = dayBuckets(todayStart);
  const yesterday = dayBuckets(yesterdayStart);

  const lastNDays = (n: number): HourBucket[][] => {
    const out: HourBucket[][] = [];
    for (let i = 1; i <= n; i++) {
      const d = new Date(todayStart);
      d.setDate(d.getDate() - i);
      out.push(dayBuckets(d));
    }
    return out;
  };

  const d7 = lastNDays(7);
  const d15 = lastNDays(15);
  const d30 = lastNDays(30);

  const hasTodayData = today.some((b) => b.orders > 0);
  const hasAnyInWindow = series
    ? series.some((b) => (Number(b.order_items) || 0) > 0)
    : input.orders.some((o) => {
    if (!o.purchase_date) return false;
    const t = Date.parse(o.purchase_date);
    if (!Number.isFinite(t)) return false;
    const oldest = new Date(todayStart);
    oldest.setDate(oldest.getDate() - 30);
    return t >= oldest.getTime() && t <= now.getTime();
  });

  const points: HourPoint[] = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    label: hourLabel(hour),
    today: today[hour],
    yesterday: yesterday[hour],
    avg7: {
      gmv: avgAcross(d7, hour, 'gmv'),
      orders: avgAcross(d7, hour, 'orders'),
      units: avgAcross(d7, hour, 'units'),
    },
    avg15: {
      gmv: avgAcross(d15, hour, 'gmv'),
      orders: avgAcross(d15, hour, 'orders'),
      units: avgAcross(d15, hour, 'units'),
    },
    avg30: {
      gmv: avgAcross(d30, hour, 'gmv'),
      orders: avgAcross(d30, hour, 'orders'),
      units: avgAcross(d30, hour, 'units'),
    },
  }));

  let currency: string | undefined;
  for (const o of input.orders) {
    if (o.currency) {
      currency = o.currency.toUpperCase();
      break;
    }
  }

  return {
    loadedAt: new Date().toISOString(),
    connectionId: input.connectionId,
    marketplaceId: input.marketplaceId,
    sellerRef: input.sellerRef,
    dayIso: isoDate(todayStart),
    dayLabel: todayStart.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }),
    points,
    // Empty UI when neither today nor historical window has LIVE rows — not blank zeros as “pattern”.
    hasTodayData: hasTodayData || hasAnyInWindow,
    truncated: input.truncated,
    currency,
    error: input.error,
    sourceNote: input.source
      ? `Orders source: ${input.source}. Hourly buckets from Gate A item-price.`
      : 'Hourly buckets from Gate A when All Orders lines exist.',
  };
}

export function metricValue(
  p: HourPoint,
  series: 'today' | 'yesterday' | 'avg7' | 'avg15' | 'avg30',
  metric: PulseMetricId,
): number | null {
  if (series === 'today') {
    if (metric === 'gmv') return p.today.gmv;
    if (metric === 'units') return p.today.units;
    return p.today.orders;
  }
  if (series === 'yesterday') {
    if (metric === 'gmv') return p.yesterday.gmv;
    if (metric === 'units') return p.yesterday.units;
    return p.yesterday.orders;
  }
  const avg = series === 'avg7' ? p.avg7 : series === 'avg15' ? p.avg15 : p.avg30;
  return avg[metric];
}

export function exportPulseCsv(
  model: SalesPulseModel,
  metric: PulseMetricId,
  seriesOn: Set<string>,
): string {
  const cols = ['hour', ...PULSE_SERIES.filter((s) => seriesOn.has(s.id)).map((s) => s.id)];
  const lines = [cols.join(',')];
  for (const p of model.points) {
    const row = [
      p.label,
      ...PULSE_SERIES.filter((s) => seriesOn.has(s.id)).map((s) => {
        const v = metricValue(p, s.id, metric);
        return v == null ? '' : String(Number(v.toFixed(4)));
      }),
    ];
    lines.push(row.join(','));
  }
  return lines.join('\n');
}

export function hourDeepLink(opts: {
  connectionId: string | null;
  dayIso: string;
  hour: number;
  marketplaceId?: string;
}): string {
  if (!opts.connectionId) return '/amazon/connect';
  const q = new URLSearchParams({
    connection: opts.connectionId,
    from: opts.dayIso,
    to: opts.dayIso,
    hour: String(opts.hour),
  });
  if (opts.marketplaceId) q.set('marketplace', opts.marketplaceId);
  return `/amazon/orders?${q.toString()}`;
}
